import { supabase } from './supabase';

// Storage API is served by the Supabase Edge Function `r2-storage` (on *.supabase.co),
// not the Cloudflare Worker. The worker lived on *.workers.dev, which some networks
// (e.g. the farmark.co.za office) block at the DNS level, causing "Failed to fetch" on
// upload. The browser now only talks to Supabase; the function reaches R2 server-side.
const SUPABASE_URL = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').replace(/\/$/, '');
const ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';
const STORAGE_API = `${SUPABASE_URL}/functions/v1/r2-storage`;
const PUBLIC_URL = ((import.meta.env.VITE_R2_PUBLIC_URL as string | undefined) ?? '').replace(/\/$/, '');

async function getAccessToken(): Promise<string> {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
}

async function authHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return { Authorization: `Bearer ${token}`, apikey: ANON_KEY, ...extra };
}

const IMAGE_MARKER = '/r2-storage/image/';

// Canonical stored form (no apikey) — what we persist in entries.image_urls.
export function buildPublicUrl(key: string): string {
  return `${STORAGE_API}/image/${encodeURIComponent(key)}`;
}

// Browser-loadable form. <img>/fetch can't send headers, so the public anon key
// (already shipped in the bundle) goes in the query string. Works for legacy
// r2.dev URLs and bare keys too, so old entries also load through the unblocked proxy.
export function getDisplayUrl(urlOrKey: string): string {
  if (!urlOrKey) return urlOrKey;
  const key = urlOrKey.startsWith('http') ? extractKeyFromUrl(urlOrKey) : urlOrKey;
  return `${STORAGE_API}/image/${encodeURIComponent(key)}?apikey=${ANON_KEY}`;
}

export function extractKeyFromUrl(url: string): string {
  if (!url.startsWith('http')) return url;
  // New proxy URL: .../r2-storage/image/<key>[?apikey=...]
  if (url.includes(IMAGE_MARKER)) {
    const after = url.slice(url.indexOf(IMAGE_MARKER) + IMAGE_MARKER.length);
    return decodeURIComponent(after.split('?')[0]);
  }
  // Legacy public R2 URL: <PUBLIC_URL>/<key>
  if (PUBLIC_URL && url.startsWith(PUBLIC_URL + '/')) return url.slice(PUBLIC_URL.length + 1);
  // Fallback: last path segment, query stripped.
  const last = url.split('/').pop() ?? url;
  return decodeURIComponent(last.split('?')[0]);
}

export async function fetchStorageUsage(): Promise<{ used: number; total: number; percentage: number }> {
  const response = await fetch(`${STORAGE_API}/usage`, {
    method: 'GET',
    headers: await authHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to fetch storage usage (${response.status}): ${(body as { error?: string }).error ?? 'Unknown'}`
    );
  }
  return response.json() as Promise<{ used: number; total: number; percentage: number }>;
}

export async function checkStorageCapacity(bytesNeeded: number): Promise<void> {
  const usage = await fetchStorageUsage();
  if (usage.used + bytesNeeded > usage.total) {
    throw new Error('Storage full: not enough space to upload these images');
  }
}

export async function uploadToR2(blob: Blob, fileName: string): Promise<string> {
  const response = await fetch(`${STORAGE_API}/upload`, {
    method: 'POST',
    headers: await authHeaders({
      'Content-Type': blob.type || 'image/jpeg',
      'X-File-Name': fileName,
    }),
    body: blob,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      `Upload failed (${response.status}): ${(body as { error?: string }).error ?? 'Unknown'}`
    );
  }
  const { key } = (await response.json()) as { key: string };
  return buildPublicUrl(key);
}

export async function deleteFromR2(urlOrKey: string): Promise<void> {
  const key = urlOrKey.startsWith('http') ? extractKeyFromUrl(urlOrKey) : urlOrKey;
  const response = await fetch(`${STORAGE_API}/delete/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Delete failed (${response.status})`);
  }
}
